/**
 * SBC1 청크 첨부 포맷 코덱 (v1)
 *
 * 명세: backend/docs/superpowers/specs/2026-08-07-jp-plan-based-large-attachment.md §2
 * 레이아웃: [헤더 36B][청크 0][청크 1]... / 청크 = iv(12B) ‖ AES-256-GCM 암호문 ‖ tag(16B)
 *
 * 순수 로직 모듈 — Worker 와 메인 스레드 양쪽에서 import 한다. Attachment/helper.js 등
 * 상위 모듈을 절대 import 하지 않는다 (의존 방향: helper → attachmentV2 단방향, §5.1).
 * 모바일(iOS/Android) 구현은 본 명세 + testVectors.json 을 상호운용성 기준으로 삼는다.
 */

export const HEADER_SIZE = 36
export const IV_SIZE = 12
export const TAG_SIZE = 16
export const SALT_SIZE = 16
export const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024 // 8 MiB 평문 청크
export const PBKDF2_ITERATIONS = 100000
// chunkSize 방어 범위 — 상한은 헤더 파싱 시 강제(비정상 거대 청크의 단일 할당 차단).
// 최소값은 코덱이 강제하지 않는다: 테스트 벡터가 결정적 재현을 위해 16B 청크를 쓴다.
// 대신 P4 업로드/다운로드 매니저가 소비 시점에 MIN_SAFE_CHUNK_SIZE 를 강제한다
// (작은 chunkSize 는 청크 수를 부풀려 decrypt 호출을 증폭 — PR #1778 리뷰).
export const MAX_CHUNK_SIZE = 64 * 1024 * 1024
export const MIN_SAFE_CHUNK_SIZE = 4096
export const KEY_LENGTH_BITS = 256

const MAGIC = [0x53, 0x42, 0x43, 0x31] // "SBC1"
const FORMAT_VERSION = 0x01
const CIPHER_AES_256_GCM = 0x01
const AAD_SIZE = 22
const UINT32_SPAN = 2 ** 32

// big-endian uint64 를 BE uint32 두 개로 기록/판독 — 바이트 레이아웃은 u64 와 동일하며
// BigInt 미사용(프로젝트 eslint env 미선언 + 정밀도는 Number.isSafeInteger 로 방어)
const setUint64 = (view, offset, value) => {
  view.setUint32(offset, Math.floor(value / UINT32_SPAN))
  view.setUint32(offset + 4, value % UINT32_SPAN)
}

const getUint64 = (view, offset) =>
  view.getUint32(offset) * UINT32_SPAN + view.getUint32(offset + 4)

export class ChunkFormatError extends Error {
  constructor(code, message) {
    super(message)
    this.name = 'ChunkFormatError'
    this.code = code
  }
}

// 키 재료는 **텍스트가 아니라 바이트 문자열**이다 — rsKey = forge.random.getBytesSync(32)
// (pUtils.js:1311-1313, 1416)라 각 문자가 0x00~0xFF. 그래서 latin1(문자당 1바이트) 해석이
// 유일하게 올바르다. TextEncoder(UTF-8)를 쓰면 0x80 이상 바이트가 2바이트로 늘어 구포맷
// 파생 키와 갈린다(PR #1778 리뷰 제안 기각 — 근거: 위 pUtils 라인).
// 255 초과는 바이트 문자열이 아니라는 뜻(예: 실제 사용자 비밀번호 오전달) — 조용히 mod 256
// 으로 잘려 디버깅 불가한 키 불일치가 되므로 명시 실패시킨다.
export const passwordToBytes = (password) => {
  const bytes = new Uint8Array(password.length)
  for (let i = 0; i < password.length; i += 1) {
    const code = password.charCodeAt(i)
    if (code > 0xff) {
      throw new ChunkFormatError(
        'BAD_KEY_MATERIAL',
        'key material must be a byte string (charCode <= 0xFF)',
      )
    }
    bytes[i] = code
  }
  return bytes
}

export const chunkCount = (totalSize, chunkSize) =>
  totalSize === 0 ? 0 : Math.ceil(totalSize / chunkSize)

// §2.3 판별 — magic 일치 + version/cipher 검증까지 통과해야 신포맷.
// 구포맷 선두는 랜덤 salt 라 우연히 magic 과 겹쳐도 version/cipher 에서 걸러 폴백된다.
export const isChunked = (bytes) => {
  if (bytes == null || bytes.length < HEADER_SIZE) {
    return false
  }
  for (let i = 0; i < MAGIC.length; i += 1) {
    if (bytes[i] !== MAGIC[i]) {
      return false
    }
  }
  return bytes[4] === FORMAT_VERSION && bytes[5] === CIPHER_AES_256_GCM
}

export const buildHeader = ({ chunkSize, totalSize, salt }) => {
  // setUint32 는 범위 초과를 mod 2^32 로 조용히 감싸고, salt 길이 불일치는 헤더를 손상시킨다
  // — P4 가 options 로 직접 넘길 수 있으므로 방어 (PR #1778 리뷰)
  if (!Number.isInteger(chunkSize) || chunkSize <= 0 || chunkSize > MAX_CHUNK_SIZE) {
    throw new ChunkFormatError('BAD_CHUNK_SIZE', `chunkSize out of range: ${chunkSize}`)
  }
  if (salt == null || salt.length !== SALT_SIZE) {
    throw new ChunkFormatError('BAD_SALT', `salt must be ${SALT_SIZE} bytes`)
  }
  const header = new Uint8Array(HEADER_SIZE)
  const view = new DataView(header.buffer)
  header.set(MAGIC, 0)
  header[4] = FORMAT_VERSION
  header[5] = CIPHER_AES_256_GCM
  view.setUint16(6, 0) // reserved
  view.setUint32(8, chunkSize)
  setUint64(view, 12, totalSize)
  header.set(salt, 20)
  return header
}

export const parseHeader = (bytes) => {
  if (!isChunked(bytes)) {
    throw new ChunkFormatError('NOT_CHUNKED', 'not an SBC1 chunked object')
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const chunkSize = view.getUint32(8)
  const totalSize = getUint64(view, 12)
  // totalSize 는 와이어 포맷상 u64 지만 JS Number 로 안전한 범위(2^53-1 ≈ 9PB)까지만 받는다
  // — 절대 상한 500MB 대비 6자리 여유라 실사용 제약 없음(설계 §2.1 주석). 초과는 위조로 간주.
  if (chunkSize === 0 || chunkSize > MAX_CHUNK_SIZE || !Number.isSafeInteger(totalSize)) {
    throw new ChunkFormatError('BAD_HEADER', 'invalid chunkSize/totalSize')
  }
  return {
    chunkSize,
    totalSize,
    salt: bytes.slice(20, HEADER_SIZE),
  }
}

// AAD = magic(4) ‖ version(1) ‖ cipher(1) ‖ chunkSize u32 ‖ chunkIndex u32 ‖ totalSize u64 (§2.2)
// 헤더 핵심 필드와 청크 인덱스를 인증에 묶어 재배열·바꿔치기·헤더 변조를 태그 실패로 검출한다.
export const buildChunkAad = ({ chunkSize, totalSize }, chunkIndex) => {
  const aad = new Uint8Array(AAD_SIZE)
  const view = new DataView(aad.buffer)
  aad.set(MAGIC, 0)
  aad[4] = FORMAT_VERSION
  aad[5] = CIPHER_AES_256_GCM
  view.setUint32(6, chunkSize)
  view.setUint32(10, chunkIndex)
  setUint64(view, 14, totalSize)
  return aad
}

// 파일당 1회 파생 후 전 청크 공유 (구포맷과 동일 파라미터 계열: SHA-256 / 100k / 32B)
export const deriveKey = async (password, salt) => {
  const material = await crypto.subtle.importKey(
    'raw',
    passwordToBytes(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS },
    material,
    { name: 'AES-GCM', length: KEY_LENGTH_BITS },
    false,
    ['encrypt', 'decrypt'],
  )
}

// iv 는 청크당 랜덤이 기본 — 테스트 벡터 재현 시에만 주입한다
export const encryptChunk = async (key, headerInfo, chunkIndex, plainChunk, iv = null) => {
  const nonce = iv || crypto.getRandomValues(new Uint8Array(IV_SIZE))
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce, additionalData: buildChunkAad(headerInfo, chunkIndex) },
    key,
    plainChunk,
  )
  const record = new Uint8Array(IV_SIZE + cipherBuf.byteLength)
  record.set(nonce, 0)
  record.set(new Uint8Array(cipherBuf), IV_SIZE)
  return record
}

export const decryptChunk = async (key, headerInfo, chunkIndex, encRecord) => {
  const nonce = encRecord.subarray(0, IV_SIZE)
  const payload = encRecord.subarray(IV_SIZE)
  try {
    const plainBuf = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: nonce, additionalData: buildChunkAad(headerInfo, chunkIndex) },
      key,
      payload,
    )
    return new Uint8Array(plainBuf)
  } catch (e) {
    // WebCrypto 는 태그 실패 사유를 숨긴 OperationError 만 던진다 — 코드로 정규화
    throw new ChunkFormatError('DECRYPT_FAILED', `chunk ${chunkIndex} authentication failed`)
  }
}

// 전체 평문 → 전체 객체 (작은 파일·테스트용 일괄 경로 — 스트리밍은 P4 매니저가 청크
// 프리미티브(encryptChunk/decryptChunk)를 직접 조합한다)
// ⚠️ 오용 주의: 평문·객체를 통째로 메모리에 올린다 — 이 함수를 대용량 파일에 쓰면 본 설계가
// 없애려는 OOM 이 그대로 재현된다. 대용량은 반드시 청크 프리미티브 + 스트리밍 경로로.
export const encodeFile = async (plainBytes, password, options = {}) => {
  const chunkSize = options.chunkSize || DEFAULT_CHUNK_SIZE
  const salt = options.salt || crypto.getRandomValues(new Uint8Array(SALT_SIZE))
  const totalSize = plainBytes.length
  const headerInfo = { chunkSize, totalSize }
  const count = chunkCount(totalSize, chunkSize)
  const key = await deriveKey(password, salt)

  const out = new Uint8Array(HEADER_SIZE + count * (IV_SIZE + TAG_SIZE) + totalSize)
  out.set(buildHeader({ chunkSize, totalSize, salt }), 0)
  let offset = HEADER_SIZE
  for (let i = 0; i < count; i += 1) {
    const plainChunk = plainBytes.subarray(i * chunkSize, Math.min((i + 1) * chunkSize, totalSize))
    const iv = options.ivs ? options.ivs[i] : null
    // 순차 직렬화가 목적 (메모리 상한 + 결정적 레이아웃)
    // eslint-disable-next-line no-await-in-loop
    const record = await encryptChunk(key, headerInfo, i, plainChunk, iv)
    out.set(record, offset)
    offset += record.length
  }
  return out
}

export const decodeFile = async (objectBytes, password) => {
  const { chunkSize, totalSize, salt } = parseHeader(objectBytes)
  const count = chunkCount(totalSize, chunkSize)
  // 절단·패딩 검출: 헤더가 선언한 크기와 실제 바이트 수가 정확히 일치해야 한다 (§14-4)
  const expectedLength = HEADER_SIZE + count * (IV_SIZE + TAG_SIZE) + totalSize
  if (objectBytes.length !== expectedLength) {
    throw new ChunkFormatError(
      'SIZE_MISMATCH',
      `expected ${expectedLength} bytes, got ${objectBytes.length}`,
    )
  }

  const key = await deriveKey(password, salt)
  const headerInfo = { chunkSize, totalSize }
  const out = new Uint8Array(totalSize)
  let inOffset = HEADER_SIZE
  let outOffset = 0
  for (let i = 0; i < count; i += 1) {
    const plainLen = Math.min(chunkSize, totalSize - outOffset)
    const recordLen = plainLen + IV_SIZE + TAG_SIZE
    const record = objectBytes.subarray(inOffset, inOffset + recordLen)
    // 순차 직렬화가 목적 (메모리 상한)
    // eslint-disable-next-line no-await-in-loop
    const plain = await decryptChunk(key, headerInfo, i, record)
    out.set(plain, outOffset)
    inOffset += recordLen
    outOffset += plainLen
  }
  return out
}
